import api from '../api/client';

export interface CostModel {
  id?: string;
  companyId?: string;
  fuelPrice: number;
  fuelConsumption: number;
  driverCost?: number;
  driverHourlyCost: number;
  depreciationPerKm: number;
  maintenancePerKm: number;
  tollPerKm: number;
  overheadPct: number;
  avgPricePerStop: number;
  createdAt?: string;
  updatedAt?: string;
}

// Estimativas padrão de custo operacional (R$)
export const DEFAULT_COST_MODEL: CostModel = {
  fuelPrice: 6.19,
  fuelConsumption: 9.1, // km/L (ex: 9.1 km por litro)
  driverHourlyCost: 22.5,
  depreciationPerKm: 0.42,
  maintenancePerKm: 0.18,
  tollPerKm: 0.08,
  overheadPct: 0.15,
  avgPricePerStop: 26.8
};

const LS_KEY = 'otimizarota-cost-model-v1';

export function loadCostModel(): CostModel {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { ...DEFAULT_COST_MODEL, ...JSON.parse(raw) };
  } catch {
    // ignore, fallback no default
  }
  return { ...DEFAULT_COST_MODEL };
}

export function saveCostModel(cm: CostModel) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(cm));
  } catch {
    // ignore
  }
}

export async function fetchCostModelApi(): Promise<CostModel> {
  try {
    const { data } = await api.get<CostModel>('/bi/cost-model');
    if (data && typeof data.fuelPrice === 'number') {
      saveCostModel(data);
      return data;
    }
  } catch (err) {
    console.warn('Fallback para cache local de custos:', err);
  }
  return loadCostModel();
}

export async function saveCostModelApi(cm: CostModel): Promise<CostModel> {
  saveCostModel(cm);
  try {
    const { data } = await api.put<CostModel>('/bi/cost-model', cm);
    if (data) {
      saveCostModel(data);
      return data;
    }
  } catch (err) {
    console.warn('Erro ao salvar custos no backend, mantido em cache local:', err);
  }
  return cm;
}

export interface RouteCostBreakdown {
  fuel: number;
  driver: number;
  depreciation: number;
  maintenance: number;
  tolls: number;
  overhead: number;
  subtotal: number;
  total: number;
  perStop: number;
}

export function calculateRouteCost(km: number, durationMin: number, stops: number, cm: CostModel, fuelConsumptionOverride?: number | null): RouteCostBreakdown {
  const durationHrs = durationMin / 60;
  const rawCons = (fuelConsumptionOverride !== undefined && fuelConsumptionOverride !== null && fuelConsumptionOverride > 0)
    ? fuelConsumptionOverride
    : (cm.fuelConsumption > 0 ? cm.fuelConsumption : 9.1);

  // Se o valor for menor que 1, interpretamos como L/km antigo (ex: 0.11), senão como km/L (ex: 9.1)
  const kmPerLiter = rawCons < 1 ? (1 / rawCons) : rawCons;
  const liters = km / kmPerLiter;
  const fuel = liters * cm.fuelPrice;

  const driver = durationHrs * cm.driverHourlyCost;
  const depreciation = km * cm.depreciationPerKm;
  const maintenance = km * cm.maintenancePerKm;
  const tolls = km * cm.tollPerKm;
  const subtotal = fuel + driver + depreciation + maintenance + tolls;
  const overhead = subtotal * cm.overheadPct;
  const total = subtotal + overhead;
  const safeStops = Math.max(1, stops);
  return { fuel, driver, depreciation, maintenance, tolls, overhead, subtotal, total, perStop: total / safeStops };
}

export function estimateRouteRevenue(stops: number, cm: CostModel): number {
  return Math.max(1, stops) * cm.avgPricePerStop;
}

export interface Profitability {
  cost: RouteCostBreakdown;
  revenue: number;
  profit: number;
  margin: number;
}

export function routeProfitability(km: number, durationMin: number, stops: number, cm: CostModel): Profitability {
  const cost = calculateRouteCost(km, durationMin, stops, cm);
  const revenue = estimateRouteRevenue(stops, cm);
  const profit = revenue - cost.total;
  const margin = revenue > 0 ? profit / revenue : 0;
  return { cost, revenue, profit, margin };
}
